/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import type { Dispatch } from 'react';
import { flushSync } from 'react-dom';
import { animateWindowSnapshots, captureWindowRects } from './window-snapshot-animation';
import { clearWindowInteraction } from './windowInteractions';
import type { DesktopWindowAction } from './windowSystem';

const layoutActions = new Set<DesktopWindowAction['type']>([
  'toggle_full_screen',
  'toggle_maximize',
  'set_maximized',
  'choose_tile',
  'drop_window',
]);

/** Snapshot all window shells once, then let the browser animate pictures rather than live
 * chart/map/table trees. There is one coordinator, regardless of which app owns the content.
 */
export function createWindowLayoutDispatcher(
  dispatch: Dispatch<DesktopWindowAction>,
  reducedMotion: () => boolean,
) {
  let current: { transition?: ViewTransition; pending: DesktopWindowAction[] | null } | undefined;
  const releasePointers = () => {
    for (const frame of document.querySelectorAll<HTMLElement>('[data-layout-snapshot-pending]')) {
      clearWindowInteraction(frame);
      delete frame.dataset.layoutSnapshotPending;
    }
  };
  const finish = (entry: typeof current) => {
    if (current !== entry) return;
    current = undefined;
    document.documentElement.removeAttribute('data-desktop-layout-transition');
  };
  const dispatchLayout: Dispatch<DesktopWindowAction> = (action) => {
    // Preserve action order if another click/focus/navigation arrives before the old picture
    // has been captured. All queued changes commit together, exactly once.
    if (current?.pending) {
      current.pending.push(action);
      current.transition?.skipTransition();
      return;
    }
    const isLayout =
      layoutActions.has(action.type) &&
      (action.type !== 'drop_window' || action.target !== undefined);
    if (!isLayout || reducedMotion() || !document.startViewTransition || document.hidden) {
      dispatch(action);
      return;
    }
    current?.transition?.skipTransition();
    const entry = {
      pending: [action] as DesktopWindowAction[] | null,
      transition: undefined as ViewTransition | undefined,
    };
    current = entry;
    if (action.type === 'drop_window') {
      const frame = document.querySelector<HTMLElement>(
        `[data-window-id="${CSS.escape(action.id)}"]`,
      );
      if (frame) frame.dataset.layoutSnapshotPending = '';
    }
    const before = captureWindowRects();
    let after = before;
    document.documentElement.setAttribute('data-desktop-layout-transition', '');
    const commit = () => {
      const actions = entry.pending;
      entry.pending = null;
      releasePointers();
      if (actions)
        flushSync(() => {
          for (const queued of actions) dispatch(queued);
        });
      after = captureWindowRects();
    };
    try {
      entry.transition = document.startViewTransition(commit);
      // Snapshot failures (e.g. a tab becoming hidden) skip animation, not the state update.
      void entry.transition.ready
        .then(() => animateWindowSnapshots(before, after))
        .catch(() => undefined);
      void entry.transition.finished.then(
        () => finish(entry),
        () => finish(entry),
      );
    } catch {
      commit();
      finish(entry);
    }
  };
  return {
    dispatch: dispatchLayout,
    dispose() {
      current?.transition?.skipTransition();
      releasePointers();
      document.documentElement.removeAttribute('data-desktop-layout-transition');
    },
  };
}
